import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useLang } from "@/i18n";
import { api, fmtErr, fmtUsd, fmtDate, fileToBase64 } from "@/lib/api";
import {
  ArrowDownToLine, ArrowUpFromLine, UploadCloud, FileCheck2, Vault,
  ShieldCheck, Globe2, FolderOpen, Scale,
} from "lucide-react";

const cardCls = "bg-white border border-[#E6EBF1] rounded-2xl shadow-[0_8px_30px_rgba(10,37,64,0.05)]";
const inputCls = "mt-1.5 bg-white border-[#E6EBF1] text-[#0A2540] focus-visible:ring-[#635BFF]/40";
const labelCls = "text-[#425466] text-xs font-medium";
const selectContentCls = "bg-white border border-[#E6EBF1] text-[#0A2540] shadow-xl";
const selectItemCls = "focus:bg-[#F6F9FC] focus:text-[#0A2540] cursor-pointer";
const ASSETS = ["USD", "BTC", "ETH", "USDT", "USDC", "BNB", "SOL", "XRP"];

// ---------------- Wallet (deposits & withdrawals) ----------------

export function WalletTab() {
  const { t } = useLang();
  const [txs, setTxs] = useState([]);
  const [kind, setKind] = useState("deposit");
  const [method, setMethod] = useState("bank");
  const [asset, setAsset] = useState("USD");
  const [amount, setAmount] = useState("");
  const [details, setDetails] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/client/transactions").then((r) => setTxs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/client/transactions", { kind, method, asset, amount: parseFloat(amount), details });
      toast.success(t("wallet.submitted"));
      setAmount(""); setDetails("");
      load();
    } catch (err) { toast.error(fmtErr(err)); } finally { setLoading(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="wallet-tab">
      <form onSubmit={submit} className={`${cardCls} p-6 space-y-4`} data-testid="wallet-form">
        <h3 className="font-display text-base font-semibold text-[#0A2540] flex items-center gap-2">
          <Vault size={17} className="text-[#635BFF]" /> {t("wallet.title")}
        </h3>
        <p className="text-xs text-slate-400 leading-relaxed">{t("wallet.subtitle")}</p>
        <div className="grid grid-cols-2 gap-2">
          {[["deposit", ArrowDownToLine], ["withdrawal", ArrowUpFromLine]].map(([k, Icon]) => (
            <button key={k} type="button" data-testid={`wallet-kind-${k}`} onClick={() => setKind(k)}
                    className={`h-10 rounded-lg text-sm font-semibold transition-all border flex items-center justify-center gap-2 ${
                      kind === k ? "bg-[#635BFF] border-[#635BFF] text-white shadow-lg shadow-[#635BFF]/25"
                                 : "border-[#E6EBF1] text-[#425466] hover:bg-[#F6F9FC]"}`}>
              <Icon size={15} /> {t(`wallet.${k === "deposit" ? "deposit" : "withdraw"}`)}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className={labelCls}>{t("wallet.method")}</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="wallet-method-select" className="mt-1.5 bg-white border-[#E6EBF1] text-[#0A2540]"><SelectValue /></SelectTrigger>
              <SelectContent className={selectContentCls}>
                <SelectItem value="bank" className={selectItemCls}>{t("wallet.bank")}</SelectItem>
                <SelectItem value="crypto" className={selectItemCls}>{t("wallet.crypto")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className={labelCls}>{t("wallet.asset")}</Label>
            <Select value={asset} onValueChange={setAsset}>
              <SelectTrigger data-testid="wallet-asset-select" className="mt-1.5 bg-white border-[#E6EBF1] text-[#0A2540]"><SelectValue /></SelectTrigger>
              <SelectContent className={selectContentCls}>
                {ASSETS.map((a) => <SelectItem key={a} value={a} className={selectItemCls}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className={labelCls}>{t("wallet.amount")}</Label>
          <Input data-testid="wallet-amount-input" type="number" min="1" step="any" required
                 value={amount} onChange={(e) => setAmount(e.target.value)} className={`${inputCls} font-ticker`} />
        </div>
        <div>
          <Label className={labelCls}>{t("wallet.details")}</Label>
          <Textarea data-testid="wallet-details-input" rows={3} value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    placeholder={method === "bank" ? t("wallet.detailsHintBank") : t("wallet.detailsHintCrypto")}
                    className={`${inputCls} resize-none`} />
        </div>
        <Button data-testid="wallet-submit-btn" type="submit" disabled={loading}
                className="w-full h-11 bg-[#635BFF] hover:bg-[#0A2540] text-white font-semibold rounded-full transition-colors">
          {loading ? t("common.loading") : t("wallet.submit")}
        </Button>
      </form>

      <div className={`${cardCls} p-6`} data-testid="wallet-history">
        <h3 className="font-display text-base font-semibold text-[#0A2540] mb-4">{t("wallet.history")}</h3>
        {txs.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center" data-testid="wallet-empty">{t("wallet.empty")}</p>
        ) : (
          <div className="space-y-3">
            {txs.map((tx) => (
              <div key={tx.id} data-testid={`wallet-tx-${tx.id}`}
                   className="border border-[#E6EBF1] rounded-xl p-4 bg-[#F6F9FC] flex items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {tx.kind === "deposit"
                      ? <ArrowDownToLine size={14} className="text-emerald-500" />
                      : <ArrowUpFromLine size={14} className="text-[#FF5996]" />}
                    <span className="text-sm font-semibold text-[#0A2540]">
                      {t(tx.kind === "deposit" ? "wallet.deposit" : "wallet.withdraw")} · {tx.asset}
                    </span>
                    <StatusBadge status={tx.status} />
                  </div>
                  <p className="text-xs font-ticker text-[#425466] mt-1.5">{fmtDate(tx.created_at)} · {tx.method === "bank" ? "SEPA" : "Crypto"}</p>
                  {tx.details && <p className="text-xs text-slate-400 mt-0.5 break-all">{tx.details}</p>}
                </div>
                <p className="font-ticker font-semibold text-[#0A2540] shrink-0">
                  {tx.asset === "USD" ? fmtUsd(tx.amount) : `${tx.amount} ${tx.asset}`}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- Shared upload card ----------------

function UploadCard({ category, title, hint, onDone }) {
  const { t } = useLang();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setLoading(true);
    try {
      const b64 = await fileToBase64(file);
      await api.post("/client/documents", { category, filename: b64.name, mime: b64.mime, data: b64.data });
      toast.success(t("kyc.uploaded"));
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      onDone();
    } catch (err) { toast.error(fmtErr(err)); } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className={`${cardCls} p-6`} data-testid={`upload-${category}`}>
      <h4 className="font-display text-sm font-semibold text-[#0A2540]">{title}</h4>
      <p className="text-xs text-slate-400 mt-1">{hint}</p>
      <div className="mt-4 flex items-center gap-3">
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
               data-testid={`file-input-${category}`} onChange={(e) => setFile(e.target.files?.[0] || null)} />
        <button type="button" onClick={() => fileRef.current?.click()} data-testid={`file-pick-${category}`}
                className="flex-1 h-20 rounded-xl border-2 border-dashed border-[#E6EBF1] hover:border-[#635BFF]/50 hover:bg-[#635BFF]/5 transition-colors flex flex-col items-center justify-center gap-1.5 text-[#425466]">
          <UploadCloud size={20} className="text-[#635BFF]" />
          <span className="text-xs font-medium truncate max-w-[220px]">{file ? file.name : t("kyc.hint")}</span>
        </button>
      </div>
      <Button type="submit" data-testid={`upload-btn-${category}`} disabled={!file || loading}
              className="mt-4 w-full h-10 bg-[#635BFF] hover:bg-[#0A2540] text-white font-semibold rounded-full transition-colors">
        {loading ? t("common.loading") : t("kyc.upload")}
      </Button>
    </form>
  );
}

function DocList({ docs, emptyKey }) {
  const { t } = useLang();
  if (!docs.length)
    return <p className="text-sm text-slate-400 py-6 text-center" data-testid="docs-empty">{t(emptyKey)}</p>;
  return (
    <div className="space-y-2.5">
      {docs.map((d) => (
        <div key={d.id} data-testid={`doc-${d.id}`}
             className="border border-[#E6EBF1] rounded-xl p-3.5 bg-[#F6F9FC] flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <FileCheck2 size={16} className="text-[#635BFF] shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-[#0A2540] truncate">{d.filename}</p>
              <p className="text-[10px] font-ticker text-slate-400">{fmtDate(d.created_at)}</p>
            </div>
          </div>
          <StatusBadge status={d.status} />
        </div>
      ))}
    </div>
  );
}

// ---------------- Identity verification (KYC) ----------------

export function VerificationTab() {
  const { t } = useLang();
  const [docs, setDocs] = useState([]);
  const load = () => api.get("/client/documents").then((r) => setDocs(r.data.filter((d) => d.category === "identity"))).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="verification-tab">
      <div className={`${cardCls} p-6`}>
        <h3 className="font-display text-base font-semibold text-[#0A2540] flex items-center gap-2">
          <ShieldCheck size={17} className="text-[#635BFF]" /> {t("kyc.title")}
        </h3>
        <p className="text-sm text-[#425466] mt-2 leading-relaxed">{t("kyc.subtitle")}</p>
        <div className="mt-5">
          <UploadCard category="identity" title={t("kyc.identity")} hint={t("kyc.hint")} onDone={load} />
        </div>
      </div>
      <div className={`${cardCls} p-6`}>
        <h3 className="font-display text-base font-semibold text-[#0A2540] mb-4">{t("kyc.identity")}</h3>
        <DocList docs={docs} emptyKey="docs.empty" />
      </div>
    </div>
  );
}

// ---------------- Documents vault ----------------

const DOC_CATEGORIES = ["fiscal", "rib", "domicile", "invoice", "other"];

export function DocumentsTab() {
  const { t } = useLang();
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState("fiscal");
  const load = () => api.get("/client/documents").then((r) => setDocs(r.data.filter((d) => d.category !== "identity"))).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="documents-tab">
      <div className={`${cardCls} p-6`}>
        <h3 className="font-display text-base font-semibold text-[#0A2540] flex items-center gap-2">
          <FolderOpen size={17} className="text-[#635BFF]" /> {t("docs.title")}
        </h3>
        <p className="text-sm text-[#425466] mt-2 leading-relaxed">{t("docs.subtitle")}</p>
        <div className="mt-4">
          <Label className={labelCls}>{t("admin.type")}</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="doc-category-select" className="mt-1.5 bg-white border-[#E6EBF1] text-[#0A2540]"><SelectValue /></SelectTrigger>
            <SelectContent className={selectContentCls}>
              {DOC_CATEGORIES.map((c) => <SelectItem key={c} value={c} className={selectItemCls}>{t(`docs.${c}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-4">
          <UploadCard category={category} title={t(`docs.${category}`)} hint={t("kyc.hint")} onDone={load} />
        </div>
      </div>
      <div className={`${cardCls} p-6`}>
        <h3 className="font-display text-base font-semibold text-[#0A2540] mb-4">{t("docs.title")}</h3>
        <DocList docs={docs} emptyKey="docs.empty" />
      </div>
    </div>
  );
}

// ---------------- Fiscalité & extraterritorialité ----------------

const FISCAL_TABS = ["foreign_account", "numbered_account", "optimized_withdrawal"];
const FISCAL_LABEL_KEYS = { foreign_account: "fiscal.tab1", numbered_account: "fiscal.tab2", optimized_withdrawal: "fiscal.tab3" };
const FISCAL_ICONS = { foreign_account: Globe2, numbered_account: Vault, optimized_withdrawal: Scale };

export function FiscalTab() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [motivation, setMotivation] = useState("");
  const [contents, setContents] = useState({ foreign_account: "", numbered_account: "", optimized_withdrawal: "" });
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/client/fiscal").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const request = async () => {
    setLoading(true);
    try {
      await api.post("/client/fiscal/request", { motivation });
      toast.success(t("fiscal.submitted"));
      load();
    } catch (err) { toast.error(fmtErr(err)); } finally { setLoading(false); }
  };

  const submitTab = async (tab) => {
    if (!contents[tab].trim()) return;
    setLoading(true);
    try {
      await api.post("/client/fiscal/submissions", { tab, content: contents[tab] });
      toast.success(t("fiscal.submitted"));
      setContents({ ...contents, [tab]: "" });
      load();
    } catch (err) { toast.error(fmtErr(err)); } finally { setLoading(false); }
  };

  const status = data?.request?.status || "none";

  return (
    <div className="space-y-6" data-testid="fiscal-tab">
      <div className={`${cardCls} p-6`}>
        <h3 className="font-display text-base font-semibold text-[#0A2540] flex items-center gap-2">
          <Scale size={17} className="text-[#635BFF]" /> {t("fiscal.title")}
        </h3>
        <p className="text-sm text-[#425466] mt-2 leading-relaxed">{t("fiscal.subtitle")}</p>

        {status === "none" && (
          <div className="mt-5 space-y-4" data-testid="fiscal-request-block">
            <Textarea data-testid="fiscal-motivation-input" rows={4} value={motivation}
                      onChange={(e) => setMotivation(e.target.value)} placeholder={t("fiscal.motivation")}
                      className={`${inputCls} resize-none`} />
            <Button data-testid="fiscal-request-btn" onClick={request} disabled={loading}
                    className="bg-[#635BFF] hover:bg-[#0A2540] text-white h-11 px-7 font-semibold rounded-full transition-colors">
              {loading ? t("common.loading") : t("fiscal.requestBtn")}
            </Button>
          </div>
        )}
        {status === "requested" && (
          <div className="mt-5 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700" data-testid="fiscal-pending">
            {t("fiscal.requested")}
          </div>
        )}
        {status === "rejected" && (
          <div className="mt-5 bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-600" data-testid="fiscal-rejected">
            {t("fiscal.rejected")}
          </div>
        )}
        {status === "active" && (
          <p className="mt-5 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="fiscal-active">
            {t("fiscal.activeIntro")}
          </p>
        )}
      </div>

      {status === "active" && (
        <div className={`${cardCls} p-6`} data-testid="fiscal-forms">
          <Tabs defaultValue="foreign_account">
            <TabsList className="bg-[#F6F9FC] border border-[#E6EBF1] p-1 rounded-xl h-auto flex-wrap">
              {FISCAL_TABS.map((tab) => {
                const Icon = FISCAL_ICONS[tab];
                return (
                  <TabsTrigger key={tab} value={tab} data-testid={`fiscal-subtab-${tab}`}
                               className="data-[state=active]:bg-[#635BFF] data-[state=active]:text-white text-[#425466] rounded-lg gap-2">
                    <Icon size={14} /> {t(FISCAL_LABEL_KEYS[tab])}
                  </TabsTrigger>
                );
              })}
            </TabsList>
            {FISCAL_TABS.map((tab) => (
              <TabsContent key={tab} value={tab} className="mt-5 space-y-4">
                <Label className={labelCls}>{t("fiscal.contentLabel")}</Label>
                <Textarea data-testid={`fiscal-content-${tab}`} rows={6} value={contents[tab]}
                          onChange={(e) => setContents({ ...contents, [tab]: e.target.value })}
                          className={`${inputCls} resize-none mt-1.5`} />
                <Button data-testid={`fiscal-submit-${tab}`} onClick={() => submitTab(tab)} disabled={loading}
                        className="bg-[#635BFF] hover:bg-[#0A2540] text-white h-10 px-6 font-semibold rounded-full transition-colors">
                  {t("fiscal.submit")}
                </Button>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      )}

      {data?.submissions?.length > 0 && (
        <div className={`${cardCls} p-6`} data-testid="fiscal-submissions">
          <h3 className="font-display text-base font-semibold text-[#0A2540] mb-4">{t("fiscal.mySubmissions")}</h3>
          <div className="space-y-3">
            {data.submissions.map((s) => (
              <div key={s.id} className="border border-[#E6EBF1] rounded-xl p-4 bg-[#F6F9FC]" data-testid={`fiscal-sub-${s.id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-sm font-semibold text-[#0A2540]">{t(FISCAL_LABEL_KEYS[s.tab] || s.tab)}</span>
                  <StatusBadge status={s.status} />
                </div>
                <p className="text-xs text-[#425466] mt-2 whitespace-pre-line leading-relaxed">{s.content}</p>
                <p className="text-[10px] font-ticker text-slate-400 mt-2">{fmtDate(s.created_at)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
